import { InputHelperText } from "components/FormElements/InputHelperText";
import { InputWrapper } from "components/FormElements/InputWrapper";
import { Label } from "components/FormElements/Label";
import React, { InputHTMLAttributes } from "react";

type AvailableInputTypes =
  | "date"
  | "datetime-local"
  | "email"
  | "number"
  | "password"
  | "search"
  | "tel"
  | "text"
  | "time"
  | "url";
export interface BaseInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Name is used in the input elements id and labels elements for values to create a unique
   * label + input pair. If the name is not unique it will cause accessibility issues.
   * Usually you want to name the input after the data it will contain e.g mobile-number,
   * first-name, preferred-name etc. */
  name: string;
  /** Accepts most valid html input types */
  type: AvailableInputTypes;
  /** Optional placeholder text for an input */
  placeholderText?: string;
  /** Error text sets the input aria-invalid to true and provides error text */
  errorText?: string;
  /** Optional helper text for an input. It will turn red if the field is in error */
  helperText?: string;
  /** The default width is set to match the inputs size to its content.
   * Full width sets the input to have a width of 100% of its parent element. */
  inputWidth?: "default" | "full";
  /** Only use "large" for the sign up form and marketing site */
  inputStyle?: "default" | "large";
  labelText: string;
  /** Standard HTML input required attribute. When not present or false `(optional)` text will display in the inputs label. */
  required?: boolean;
  /** Extra classes to e.g. change the background colour */
  extraClasses?: string;
  /** Move input alignment to the right for displaying things numbers only (e.g. currency) */
  alignment?: "left" | "right";
}

export const BaseInput: React.FC<BaseInputProps> = ({
  alignment,
  name,
  placeholderText,
  type,
  required,
  disabled,
  errorText,
  helperText,
  inputWidth,
  inputStyle = "default",
  labelText,
  extraClasses,
  ...props
}) => {
  const ariaDescribedBy =
    errorText || helperText ? `${name}Description` : undefined;
  const inError = errorText ? true : undefined;

  return (
    <InputWrapper
      inputStyle={inputStyle}
      inputWidth={inputWidth}
      extraClasses={extraClasses}
      error={inError}
      disabled={disabled}
      alignment={alignment}
    >
      <Label name={name} required={required}>
        {labelText}
      </Label>
      <input
        id={name}
        name={name}
        placeholder={placeholderText}
        className="input"
        disabled={disabled}
        aria-disabled={disabled}
        aria-invalid={inError}
        aria-describedby={ariaDescribedBy}
        required={required}
        type={type}
        {...props}
      />
      {ariaDescribedBy && (
        <InputHelperText
          name={ariaDescribedBy}
          helperText={helperText}
          errorText={errorText}
        />
      )}
    </InputWrapper>
  );
};
